import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';

import {
  isUserActionNeedPhotoVerification,
  needsVerification,
  NotificationManager,
  PhotoVerificationAction,
  SiteConfigManager,
  useApplicationState,
  useCasinoFavorites,
  useCasinoNewState,
} from '@atlas/core';
import { casinoRoutes, staticRoutes } from '@atlas/routing';
import { CASINO_TILE_TYPES, CasinoGameTile } from '@atlas/ui';

import { ACCOUNT_STATUSES } from 'common/constants';
import { CDN_TYPE_GAME, imageUrlCDN, normalizePath } from 'common/utils/utils';
import { useAccountBlocked } from 'hooks/useAccountBlocked';
import { useImageValidation } from 'hooks/useImageValidation';
import useSignInRedirects from 'src/components/Auth/useSignInRedirects';
import { useSignupPopup } from 'src/components/Auth/useSignUpPopup';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';
import { getGameIdentifier } from 'src/helpers/casino';

const { game: casinoGamePath } = casinoRoutes;

const GameTile = ({
  className,
  game,
  imageFetchPriority = 'auto',
  imageLoading = 'lazy',
  imageOverride,
  isFavorites,
  isLoading,
  showOverlay,
  size = 'M',
  toggleShowOverlay,
}) => {
  const { redirectToSignIn } = useSignInRedirects();
  const { openSignupPopup } = useSignupPopup();
  const applicationState = useApplicationState();
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();
  const { account, authenticated, bankAccounts } = applicationState;
  const {
    addToFavorites,
    favoriteGames: { data: favoriteGames },
    removeFromFavorites,
  } = useCasinoNewState();
  const { removeFromFavorites: removeFavoriteGameFromFavorites } = useCasinoFavorites();
  const {
    location: { pathname, search },
    push: historyPush,
  } = useHistory();
  const showInPopup = SiteConfigManager.getConfig('config.config.registration.showInPopup', false);

  const {
    demoAvailable,
    iconUrl,
    id,
    isDemoAvailable, // only for classic page
    name, //only for home page
    providerDisplayedName,
    providerName,
  } = game;
  const failedOverride = useImageValidation(imageOverride);
  const defaultIcon = iconUrl || game?.icon || game?.imageUrl || game?.image;
  const image =
    (!failedOverride && imageOverride) ||
    (defaultIcon ? imageUrlCDN(defaultIcon, CDN_TYPE_GAME) : null);
  const gameIdentifier = getGameIdentifier(game);
  const isAccountBlocked = useAccountBlocked();

  const casinoGameTileType = SiteConfigManager.getConfig(
    'config.config.general.mobileCasinoGameTileView',
    CASINO_TILE_TYPES.TYPE_5,
  );
  const demoButtonStateForSignedInUsers = SiteConfigManager.getConfig(
    'config.config.casino.mobile_demo_logged',
    false,
  );
  const demoButtonStateForNonSignedInUsers = SiteConfigManager.getConfig(
    'config.config.casino.mobile_demo_non_logged',
    false,
  );

  const toggleOverlay = useCallback(() => {
    toggleShowOverlay(game);
  }, [game, toggleShowOverlay]);

  const play = useCallback(
    async (isDemo = false) => {
      if (isAccountBlocked) {
        NotificationManager.display({
          id: 'accountBlocked',
          type: 'accountBlocked',
        });
        return;
      }

      if (
        authenticated &&
        needsVerification(account, bankAccounts) &&
        isUserActionNeedPhotoVerification(account, PhotoVerificationAction.CASINO_GAME)
      ) {
        return NotificationManager.display({
          message: 'verificationPopup',
          type: 'verificationPopup',
        });
      }

      const cleanedPath = normalizePath(pathname);
      const gameUrl = new URL(`${cleanedPath}/${casinoGamePath}/${game.id}`, location.origin);
      gameUrl.searchParams.set('provider', providerName);

      if (!authenticated && !isDemo) {
        redirectToSignIn({
          casinoGameId: game.id,
          providerName,
          redirectPath: gameUrl.pathname + gameUrl.search,
        });
        return;
      }

      const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
      if (!canProceedSpentLimit) return;

      const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
      if (!canProceedSelfRegulation) return;

      if (account.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED) {
        if (showInPopup) {
          openSignupPopup();
        } else {
          historyPush(staticRoutes.signup);
        }
        return;
      }

      gameUrl.searchParams.set('from', pathname + search);

      if (isDemo) {
        gameUrl.searchParams.set('demo', 'true');
      }

      historyPush(gameUrl.pathname + gameUrl.search);
    },
    [
      isAccountBlocked,
      authenticated,
      account,
      bankAccounts,
      showSpentLimitPopupIfNeeded,
      showSelfRegulationPopupIfNeeded,
      pathname,
      game.id,
      providerName,
      search,
      historyPush,
      redirectToSignIn,
      showInPopup,
      openSignupPopup,
    ],
  );

  const isFavoriteGame = useMemo(() => favoriteGames.includes(game.id), [favoriteGames, game.id]);

  const onPlayGame = useCallback(
    e => {
      e.stopPropagation();
      play();
    },
    [play],
  );

  const toggleFavorite = useCallback(async () => {
    if (!authenticated) {
      redirectToSignIn({ redirectPath: `${pathname}${search}` });
      return;
    }

    if (isFavorites) {
      return removeFavoriteGameFromFavorites(id);
    }

    if (isFavoriteGame) {
      removeFromFavorites(id);
    } else {
      addToFavorites(id);
    }
  }, [
    addToFavorites,
    authenticated,
    id,
    isFavoriteGame,
    isFavorites,
    pathname,
    search,
    removeFavoriteGameFromFavorites,
    removeFromFavorites,
    redirectToSignIn,
  ]);

  const demo = useMemo(() => {
    if (
      (authenticated && !demoButtonStateForSignedInUsers) ||
      (!authenticated && !demoButtonStateForNonSignedInUsers)
    ) {
      return undefined;
    }
    // TODO: remove hardcode here
    // Should be authenticated for Evolution games
    if ((isDemoAvailable || demoAvailable) && (providerName !== 'Evolution' || authenticated)) {
      return {
        onClick: e => {
          e.stopPropagation();
          play(true);
        },
      };
    }
    return undefined;
  }, [
    authenticated,
    demoButtonStateForSignedInUsers,
    demoButtonStateForNonSignedInUsers,
    isDemoAvailable,
    demoAvailable,
    providerName,
    play,
  ]);

  return (
    <div className={cx(className)}>
      <CasinoGameTile
        demo={demo}
        favorite={{
          onClick: toggleFavorite,
          state: isFavorites || isFavoriteGame,
        }}
        image={image}
        imageFetchPriority={imageFetchPriority}
        imageLoading={imageLoading}
        isLoading={isLoading}
        isMobile
        key={gameIdentifier}
        name={name}
        onClickPlay={onPlayGame}
        provider={providerDisplayedName}
        showOverlay={showOverlay}
        size={size}
        toggleShowOverlay={toggleOverlay}
        type={casinoGameTileType}
      />
    </div>
  );
};

GameTile.propTypes = {
  className: PropTypes.string,
  game: PropTypes.shape({
    icon: PropTypes.string,
    iconUrl: PropTypes.string,
    id: PropTypes.number,
    image: PropTypes.string,
    imageUrl: PropTypes.string,
  }).isRequired,
  imageOverride: PropTypes.string,
  isFavorites: PropTypes.bool,
};

export default GameTile;
